import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TagList } from '@/components/widget/tag-list';
import { TagSelect } from '@/components/widget/tag-select';
import { cn } from '@/lib/utils';
import type { DocumentExportFormat } from '@/api/content-api';
import { useDocumentRender } from '@/hooks/useContentQueries';
import { Content } from '@vivport/backend/types';
import { FileDown, Loader2, RefreshCcw } from 'lucide-react';
import { DocumentHierarchyInfoButton } from './DocumentHierarchyInfoButton';

interface DetailsContentActionButtonsProps {
  content: Content;
  isRegenerating: boolean;
  isExporting: boolean;
  handleExport: (format: DocumentExportFormat) => void;
  handleRegenerate: () => void;
  handleToggleMessageDraftComposer: () => void;
  handleToggleMessagePromotePanel: () => void;
  handleRunMessageExtraction: () => void;
}

export function ExportMenu({
  isExporting,
  handleExport,
}: {
  isExporting: boolean;
  handleExport: (format: DocumentExportFormat) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={isExporting}
          className="relative"
          aria-busy={isExporting}
          aria-label={isExporting ? 'Exporting…' : 'Export document'}
          title="Export"
          data-testid="export-menu-trigger"
        >
          <span className={cn(isExporting && 'invisible')} aria-hidden={isExporting}>
            <FileDown className="h-4 w-4" />
          </span>
          {isExporting ? (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
              <Loader2 className="h-4 w-4 animate-spin" />
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="export-option-pdf" onClick={() => handleExport('pdf')}>PDF (.pdf)</DropdownMenuItem>
        <DropdownMenuItem data-testid="export-option-docx" onClick={() => handleExport('docx')}>Word (.docx)</DropdownMenuItem>
        <DropdownMenuItem data-testid="export-option-markdown" onClick={() => handleExport('markdown')}>Markdown (.md)</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DetailsContentActionButtons({
  content,
  isRegenerating,
  isExporting,
  handleExport,
  handleRegenerate,
  handleToggleMessageDraftComposer,
  handleToggleMessagePromotePanel,
  handleRunMessageExtraction,
}: DetailsContentActionButtonsProps) {
    const tagIds: number[] = [];

    // A definition or instance root's body belongs to the template workflow, never to
    // description regeneration — the render's template field is the backend's say-so.
    const documentRootId =
      content.contentType === 'DOCUMENT' ? (content.hierarchyTree?.id ?? content.id) : 0;
    const renderQuery = useDocumentRender(documentRootId, {
      enabled: content.contentType === 'DOCUMENT',
    });
    const carriesTemplate =
      content.contentType === 'DOCUMENT' && renderQuery.data?.template != null;

    const showRegenerate =
      content.contentType !== 'NEO_CHAT' &&
      content.contentType !== 'MESSAGE' &&
      content.contentType !== 'MANUAL_SESSION' &&
      content.contentType !== 'VIDEO' &&
      content.contentType !== 'TEMPLATE' &&
      !carriesTemplate;
    const showMessageThreadActions = content.contentType === 'MESSAGE';

    return (
      <>
        {/* Large devices: compact layout */}
        <div className="hidden lg:flex items-center gap-2">
          <TagList tagIds={tagIds} />
          {content.contentType === 'DOCUMENT' ? <DocumentHierarchyInfoButton document={content} /> : null}
          <TagSelect contentId={content.id} />
          {content.contentType === 'DOCUMENT' ? (
            <ExportMenu isExporting={isExporting} handleExport={handleExport} />
          ) : null}
          {showRegenerate ? (
            <Button
              type="button"
              onClick={handleRegenerate}
              variant="secondary"
              disabled={isRegenerating}
              className="relative"
              aria-busy={isRegenerating}
              aria-label={isRegenerating ? 'Regenerating description…' : 'Regenerate description'}
            >
              <span
                className={cn('inline-flex items-center', isRegenerating && 'invisible')}
                aria-hidden={isRegenerating}
              >
                <RefreshCcw className="mr-2 h-4 w-4" />
                Regenerate
              </span>
              {isRegenerating ? (
                <span
                  className="pointer-events-none absolute inset-0 flex items-center justify-center"
                  aria-hidden
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              ) : null}
            </Button>
          ) : null}
          {showMessageThreadActions ? (
            <>
              <Button size="sm" variant="outline" onClick={handleToggleMessageDraftComposer}>
                Compose Draft
              </Button>
              <Button size="sm" variant="outline" onClick={handleToggleMessagePromotePanel}>
                Promote to Document
              </Button>
              <Button size="sm" variant="outline" onClick={handleRunMessageExtraction}>
                Extract
              </Button>
            </>
          ) : null}
        </div>

        {/* Small/Medium devices: full-width layout with icon-only button */}
        <div className="lg:hidden flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <TagList tagIds={tagIds} />
            {content.contentType === 'DOCUMENT' ? <DocumentHierarchyInfoButton document={content} /> : null}
            <TagSelect contentId={content.id} />
            {showMessageThreadActions ? (
              <>
                <Button size="sm" variant="outline" onClick={handleToggleMessageDraftComposer}>
                  Compose Draft
                </Button>
                <Button size="sm" variant="outline" onClick={handleToggleMessagePromotePanel}>
                  Promote to Document
                </Button>
                <Button size="sm" variant="outline" onClick={handleRunMessageExtraction}>
                  Extract
                </Button>
              </>
            ) : null}
          </div>
          {(content.contentType === 'DOCUMENT' || showRegenerate) ? (
            <div className="flex items-center gap-2">
              {content.contentType === 'DOCUMENT' ? (
                <ExportMenu isExporting={isExporting} handleExport={handleExport} />
              ) : null}
              {showRegenerate ? (
                <Button
                  type="button"
                  onClick={handleRegenerate}
                  variant="secondary"
                  disabled={isRegenerating}
                  size="icon"
                  className="relative"
                  aria-busy={isRegenerating}
                  aria-label={isRegenerating ? 'Regenerating description…' : 'Regenerate description'}
                >
                  <span className={cn(isRegenerating && 'invisible')} aria-hidden={isRegenerating}>
                    <RefreshCcw className="h-4 w-4" />
                  </span>
                  {isRegenerating ? (
                    <span
                      className="pointer-events-none absolute inset-0 flex items-center justify-center"
                      aria-hidden
                    >
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </span>
                  ) : null}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </>
    );
}
